import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import {
  Card,
  type CardImagePadding,
  type CardImageProps,
  type CardProps,
  type ImageAspectRatio,
} from '@primer/react-brand/esm'

import {documentToPlainTextString, getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {getImageSources} from '../../../lib/utils/images'
import {t} from '../../../lib/utils/localization'
import type {PrimerComponentCard} from '../../../schemas/contentful/contentTypes/primerComponentCard'

import {codeMarkRenderer, renderSingleLineRichText} from '../shared/RichTextFieldRenderer'

export type ContentfulCardProps = {
  component: PrimerComponentCard
  variant?: CardProps['variant']
  backgroundColor?: CardProps['backgroundColor']
  hasBorder?: boolean
  className?: string
  fullWidth?: boolean
  imageAspectRatio?: ImageAspectRatio
  imagePosition?: CardImageProps['position']
  imagePadding?: CardImagePadding
  imageLoading?: CardImageProps['loading']
  imageWidth?: number
  imageHeight?: number
  ctaVariant?: CardProps['ctaVariant']
}

export const ContentfulCard = ({
  component,
  hasBorder = false,
  variant = component.fields.variant,
  backgroundColor = component.fields.backgroundColor,
  fullWidth = component.fields.fullWidth ?? false,
  imageAspectRatio = component.fields.imageAspectRatio,
  imagePosition = component.fields.imagePosition,
  imagePadding = component.fields.imagePadding,
  imageLoading = component.fields.imageLoading,
  imageWidth = component.fields.imageWidth,
  imageHeight = component.fields.imageHeight,
  ctaVariant = component.fields.ctaVariant,
  className,
}: ContentfulCardProps) => {
  const Octicon = getPrimerIcon(component.fields.icon)
  const image = component.fields.image
  const iconColor = component.fields.iconColor || 'default'
  const hasBackground = component.fields.iconBackground
  const label = component.fields.label
  const labelVariant = component.fields.labelVariant || 'token'
  const ctaText = component.fields.ctaText || t('Learn more')
  const plainTextHeading =
    component.fields.heading ??
    (component.fields.headingRich ? documentToPlainTextString(component.fields.headingRich) : '')

  return (
    <Card
      data-ref={`card-action-${component.sys.id}`}
      align={component.fields.align}
      href={component.fields.href}
      ctaText={ctaText}
      ctaVariant={ctaVariant}
      className={className}
      hasBorder={hasBorder}
      backgroundColor={backgroundColor}
      fullWidth={fullWidth}
      variant={variant}
      {...getAnalyticsEvent({
        action: 'learn more',
        tag: 'card',
        context: plainTextHeading,
      })}
    >
      {Octicon && (
        <Card.Icon
          data-testid={`${component.fields.icon}-${component.fields.href}`}
          // eslint-disable-next-line react-hooks/static-components
          icon={<Octicon />}
          color={iconColor}
          hasBackground={hasBackground}
        />
      )}

      <Card.Heading as={component.fields.headingLevel}>
        {component.fields.headingRich
          ? renderSingleLineRichText(component.fields.headingRich)
          : component.fields.heading}
      </Card.Heading>

      {image && (
        <Card.Image
          as="picture"
          src={`${image.fields.file.url}?fm=webp`}
          sources={getImageSources(image.fields.file.url)}
          alt={image.fields.description || ''}
          aspectRatio={imageAspectRatio}
          position={imagePosition}
          padding={imagePadding}
          loading={imageLoading}
          width={imageAspectRatio ? undefined : imageWidth}
          height={imageAspectRatio ? undefined : imageHeight}
        />
      )}

      {label &&
        (typeof label === 'string' ? (
          <Card.Label>{label}</Card.Label>
        ) : (
          <Card.Label variant={labelVariant}>{label.fields.text}</Card.Label>
        ))}

      {component.fields.description && (
        <Card.Description>
          {documentToReactComponents(component.fields.description, {
            renderMark: codeMarkRenderer,
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => [children, <br key="line-break" />],
            },
          })}
        </Card.Description>
      )}
    </Card>
  )
}
